/**
 * ========================================
 * ERROR TYPES & DEFINITIONS
 * ========================================
 * Comprehensive error handling types for the application
 */

/**
 * API Error Response from Backend
 */
export interface APIErrorResponse {
  status: number;
  detail?: string;
  message?: string;
  error?: string;
  code?: string;
  errors?: Record<string, string | string[]>;
}

/**
 * Standardized Application Error
 */
export interface AppError {
  code: string;
  message: string;
  userMessage: string;
  status: number;
  details?: Record<string, any>;
  timestamp: string;
  isRecoverable?: boolean;
}

/**
 * Error Categories
 */
export enum ErrorCategory {
  VALIDATION = 'VALIDATION',
  AUTHENTICATION = 'AUTHENTICATION',
  AUTHORIZATION = 'AUTHORIZATION',
  NOT_FOUND = 'NOT_FOUND',
  CONFLICT = 'CONFLICT',
  SERVER_ERROR = 'SERVER_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Common Error Codes
 */
export enum ErrorCode {
  // Auth Errors
  EMAIL_ALREADY_EXISTS = 'EMAIL_ALREADY_EXISTS',
  EMAIL_REGISTERED = 'EMAIL_REGISTERED',
  USERNAME_ALREADY_EXISTS = 'USERNAME_ALREADY_EXISTS',
  PHONE_NUMBER_ALREADY_EXISTS = 'PHONE_NUMBER_ALREADY_EXISTS',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  UNAUTHORIZED = 'UNAUTHORIZED',
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  INVALID_TOKEN = 'INVALID_TOKEN',

  // Validation Errors
  VALIDATION_FAILED = 'VALIDATION_FAILED',
  INVALID_EMAIL = 'INVALID_EMAIL',
  INVALID_PASSWORD = 'INVALID_PASSWORD',
  INVALID_PHONE_NUMBER = 'INVALID_PHONE_NUMBER',
  WEAK_PASSWORD = 'WEAK_PASSWORD',
  REQUIRED_FIELD_MISSING = 'REQUIRED_FIELD_MISSING',

  // Resource Errors
  RESOURCE_NOT_FOUND = 'RESOURCE_NOT_FOUND',
  RESOURCE_ALREADY_EXISTS = 'RESOURCE_ALREADY_EXISTS',
  RESOURCE_DELETED = 'RESOURCE_DELETED',

  // Permission Errors
  FORBIDDEN = 'FORBIDDEN',
  INSUFFICIENT_PERMISSIONS = 'INSUFFICIENT_PERMISSIONS',

  // Server Errors
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  DATABASE_ERROR = 'DATABASE_ERROR',

  // Network Errors
  NETWORK_TIMEOUT = 'NETWORK_TIMEOUT',
  NETWORK_FAILED = 'NETWORK_FAILED',
  REQUEST_FAILED = 'REQUEST_FAILED',

  // Generic
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * Error Messages Mapping
 * Maps error codes to user-friendly messages
 */
export const ERROR_MESSAGES: Record<string, string> = {
  // Authentication Errors
  [ErrorCode.EMAIL_ALREADY_EXISTS]: 'Email already registered. Please use a different email or try logging in.',
  [ErrorCode.EMAIL_REGISTERED]: 'This email is already registered. Please use a different email or try logging in.',
  [ErrorCode.USERNAME_ALREADY_EXISTS]: 'Username already taken. Please choose a different username.',
  [ErrorCode.PHONE_NUMBER_ALREADY_EXISTS]: 'This phone number is already registered. Please use a different phone number.',
  [ErrorCode.INVALID_CREDENTIALS]: 'Invalid email or password. Please try again.',
  [ErrorCode.UNAUTHORIZED]: 'You are not authenticated. Please login to continue.',
  [ErrorCode.TOKEN_EXPIRED]: 'Your session has expired. Please login again.',
  [ErrorCode.INVALID_TOKEN]: 'Invalid session. Please login again.',

  // Validation Errors
  [ErrorCode.VALIDATION_FAILED]: 'Please check your input and try again.',
  [ErrorCode.INVALID_EMAIL]: 'Please enter a valid email address.',
  [ErrorCode.INVALID_PASSWORD]: 'Password must be at least 8 characters with uppercase, lowercase, number, and special character.',
  [ErrorCode.INVALID_PHONE_NUMBER]: 'Please enter a valid phone number (e.g., +1 (555) 123-4567).',
  [ErrorCode.WEAK_PASSWORD]: 'Password is too weak. Please use a stronger password.',
  [ErrorCode.REQUIRED_FIELD_MISSING]: 'Please fill in all required fields.',


  // Field-specific validation errors (new)
  'INVALID_DATE': 'Please enter a valid date.',
  'INVALID_NAME': 'Please enter a valid name.',
  'INVALID_ADDRESS': 'Please enter a valid address.',
  'INVALID_CITY': 'Please enter a valid city name.',
  'INVALID_STATE': 'Please enter a valid state/province.',
  'INVALID_COUNTRY': 'Please enter a valid country.',
  'INVALID_PINCODE': 'Please enter a valid postal/zip code.',
  'INVALID_GENDER': 'Please select a valid gender.',

  // Resource Errors
  [ErrorCode.RESOURCE_NOT_FOUND]: 'The requested resource was not found.',
  [ErrorCode.RESOURCE_ALREADY_EXISTS]: 'This resource already exists.',
  [ErrorCode.RESOURCE_DELETED]: 'This resource has been deleted.',

  // Permission Errors
  [ErrorCode.FORBIDDEN]: 'You do not have permission to perform this action.',
  [ErrorCode.INSUFFICIENT_PERMISSIONS]: 'Insufficient permissions to access this resource.',

  // Server Errors
  [ErrorCode.INTERNAL_SERVER_ERROR]: 'Server error occurred. Please try again later.',
  [ErrorCode.SERVICE_UNAVAILABLE]: 'Service is temporarily unavailable. Please try again later.',
  [ErrorCode.DATABASE_ERROR]: 'Database error occurred. Please try again later.',

  // Network Errors
  [ErrorCode.NETWORK_TIMEOUT]: 'Request timed out. Please check your internet connection.',
  [ErrorCode.NETWORK_FAILED]: 'Network request failed. Please check your internet connection.',
  [ErrorCode.REQUEST_FAILED]: 'Request failed. Please try again.',

  // Generic
  [ErrorCode.UNKNOWN_ERROR]: 'Something went wrong. Please try again later.',
};

/**
 * HTTP Status Code to Error Code Mapping
 */
export const HTTP_STATUS_ERROR_MAP: Record<number, string> = {
  400: ErrorCode.VALIDATION_FAILED,
  401: ErrorCode.UNAUTHORIZED,
  403: ErrorCode.FORBIDDEN,
  404: ErrorCode.RESOURCE_NOT_FOUND,
  409: ErrorCode.RESOURCE_ALREADY_EXISTS,
  422: ErrorCode.VALIDATION_FAILED,
  429: ErrorCode.REQUEST_FAILED,
  500: ErrorCode.INTERNAL_SERVER_ERROR,
  503: ErrorCode.SERVICE_UNAVAILABLE,
};

/**
 * Specific Error Messages for Known Backend Errors
 * Maps backend error messages to user-friendly messages
 */
export const BACKEND_ERROR_PATTERNS: Array<{
  pattern: RegExp;
  code: string;
  message: string;
}> = [
  // Duplicate constraint violations - CHECK FIRST before other patterns
  {
    pattern: /duplicate.*constraint.*phone_number|phone_number.*already.*exists|uniqueviolation.*phone/i,
    code: ErrorCode.PHONE_NUMBER_ALREADY_EXISTS,
    message: ERROR_MESSAGES[ErrorCode.PHONE_NUMBER_ALREADY_EXISTS],
  },
  {
    pattern: /duplicate.*constraint.*email|email.*already.*registered|uniqueviolation.*email/i,
    code: ErrorCode.EMAIL_REGISTERED,
    message: ERROR_MESSAGES[ErrorCode.EMAIL_REGISTERED],
  },
  {
    pattern: /duplicate.*constraint.*username|username.*already.*taken|uniqueviolation.*username/i,
    code: ErrorCode.USERNAME_ALREADY_EXISTS,
    message: ERROR_MESSAGES[ErrorCode.USERNAME_ALREADY_EXISTS],
  },
  {
    pattern: /unique constraint/i,
    code: ErrorCode.RESOURCE_ALREADY_EXISTS,
    message: ERROR_MESSAGES[ErrorCode.RESOURCE_ALREADY_EXISTS],
  },
  {
    pattern: /email.*already.*exist/i,
    code: ErrorCode.EMAIL_ALREADY_EXISTS,
    message: ERROR_MESSAGES[ErrorCode.EMAIL_ALREADY_EXISTS],
  },
  {
    pattern: /email.*registered/i,
    code: ErrorCode.EMAIL_ALREADY_EXISTS,
    message: ERROR_MESSAGES[ErrorCode.EMAIL_ALREADY_EXISTS],
  },
  {
    pattern: /username.*already.*exist/i,
    code: ErrorCode.USERNAME_ALREADY_EXISTS,
    message: ERROR_MESSAGES[ErrorCode.USERNAME_ALREADY_EXISTS],
  },
  {
    pattern: /username.*taken/i,
    code: ErrorCode.USERNAME_ALREADY_EXISTS,
    message: ERROR_MESSAGES[ErrorCode.USERNAME_ALREADY_EXISTS],
  },
  {
    pattern: /invalid.*credentials/i,
    code: ErrorCode.INVALID_CREDENTIALS,
    message: ERROR_MESSAGES[ErrorCode.INVALID_CREDENTIALS],
  },
  {
    pattern: /invalid.*email/i,
    code: ErrorCode.INVALID_EMAIL,
    message: ERROR_MESSAGES[ErrorCode.INVALID_EMAIL],
  },
  {
    pattern: /invalid.*password/i,
    code: ErrorCode.INVALID_PASSWORD,
    message: ERROR_MESSAGES[ErrorCode.INVALID_PASSWORD],
  },
  {
    pattern: /weak.*password/i,
    code: ErrorCode.WEAK_PASSWORD,
    message: ERROR_MESSAGES[ErrorCode.WEAK_PASSWORD],
  },
  {
    pattern: /token.*expired/i,
    code: ErrorCode.TOKEN_EXPIRED,
    message: ERROR_MESSAGES[ErrorCode.TOKEN_EXPIRED],
  },
  {
    pattern: /not.*found/i,
    code: ErrorCode.RESOURCE_NOT_FOUND,
    message: ERROR_MESSAGES[ErrorCode.RESOURCE_NOT_FOUND],
  },
  // Phone number validation pattern
  {
    pattern: /\+?\[1-9\].*phone|string should match pattern.*\\?\+.*\d.*phone/i,
    code: ErrorCode.INVALID_PHONE_NUMBER,
    message: ERROR_MESSAGES[ErrorCode.INVALID_PHONE_NUMBER],
  },
  {
    pattern: /string should match pattern.*\\?\+?\[1-9\].*\\d\{1,14\}/i,
    code: ErrorCode.INVALID_PHONE_NUMBER,
    message: ERROR_MESSAGES[ErrorCode.INVALID_PHONE_NUMBER],
  },
  // Date validation patterns
  {
    pattern: /invalid.*date|date.*invalid|date.*format/i,
    code: 'INVALID_DATE',
    message: ERROR_MESSAGES['INVALID_DATE'],
  },
  // Name validation patterns - STRICT to avoid false positives
  {
    pattern: /^invalid.*name|^name.*invalid/i,
    code: 'INVALID_NAME',
    message: ERROR_MESSAGES['INVALID_NAME'],
  },
  // Address validation
  {
    pattern: /address.*invalid|invalid.*address/i,
    code: 'INVALID_ADDRESS',
    message: ERROR_MESSAGES['INVALID_ADDRESS'],
  },
  // City validation
  {
    pattern: /city.*invalid|invalid.*city/i,
    code: 'INVALID_CITY',
    message: ERROR_MESSAGES['INVALID_CITY'],
  },
  // State validation
  {
    pattern: /state.*invalid|invalid.*state|province/i,
    code: 'INVALID_STATE',
    message: ERROR_MESSAGES['INVALID_STATE'],
  },
  // Country validation
  {
    pattern: /country.*invalid|invalid.*country/i,
    code: 'INVALID_COUNTRY',
    message: ERROR_MESSAGES['INVALID_COUNTRY'],
  },
  // Postal code validation
  {
    pattern: /pincode.*invalid|postal.*invalid|zip.*invalid|postcode|pincode/i,
    code: 'INVALID_PINCODE',
    message: ERROR_MESSAGES['INVALID_PINCODE'],
  },
  // Gender validation
  {
    pattern: /gender.*invalid|invalid.*gender/i,
    code: 'INVALID_GENDER',
    message: ERROR_MESSAGES['INVALID_GENDER'],
  },
];
